import { JSX, useRef, useState } from 'react';
import './style.scss';
import { DateTime } from 'luxon';
import { TranslationService } from '../../services/translation.service';
import { SanitizerService } from '../../services/sanitizer.service';
import FromToSelector, { type IFromToSelector, type IFromToSelectorState } from '../Shared/FromToSelector';
import { LocationType } from '../../models/location-type.enum';
import ValidationInfo from '../../validation/validation-info';
import { AnalyticsService } from '../../services/analytics-service';
import TimeModeSelector from '../Shared/TimeModeSelector';
import { TimeMode } from '../Shared/TimeModeSelector/time-mode.enum';
import type { IDateTimePickerState } from '../Shared/DateTimePicker';
import DateTimePicker from '../Shared/DateTimePicker';
import { ConfigurationService } from '../../services/configuration.service';
import { ValidatableComponent, ValidationHelper } from '../../helpers/validation.helper';
import type { IJourney } from '../../models/journey.model';
import { ApiPlaneraResaService } from '../../services/api-planera-resa/api-planera-resa.service';
import { ErrorResponseHelper } from '../../helpers/error-response.helper';
import { DateTimeRelatesTo } from '../../models/date-time-relates-to.enum';
import { JourneyHelper } from '../../helpers/journey.helper';
import Spinner from '../Shared/Spinner';
import GeneralError from '../Shared/GeneralError';
import GeneralInformation from '../Shared/GeneralInformation';
import PaginatedList from '../Shared/JourneySearchResults/PaginatedList';
import DateHeader from '../Shared/JourneySearchResults/DateHeader';
import ExpandableListItem from '../Shared/JourneySearchResults/ExpandableListItem';
import JourneySummary from '../Shared/JourneySearchResults/JourneySummary';
import TravelPlannerFilters from './components/TravelPlannerFilters';
import {
  initialTravelPlannerFilters,
  initialTravelPlannerFiltersWithBussOnDemand,
  isValidTravelPlannerFilters,
  type ITravelPlannerFilters,
} from './components/TravelPlannerFilters/models/travel-planner-filters.model';
import { TransportMode } from '../../models/transport-mode.enum';
import { InterchangeDuration } from './components/TravelPlannerFilters/models/interchange-duration.enum';
import { DateHelper } from '../../helpers/date.helper';
import { type IErrorResponse, ErrorLevel } from '../../models/error-response.model';
import { ErrorHandlerHelper } from '../../helpers/error-handler.helper';
import { Status } from '../../hooks/fetch.hook';
import { JourneySearchHelper } from '../../helpers/journey-search.helper';
import JourneyDetails from './components/JourneyDetails';
import { IPaginationLinks } from '../../models/pagination-links.model';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { MicromobilityService } from '../../services/micromobility/micromobility.service';
import MicromobilityFilters from '../Shared/MicromobilityFilters';
import { ClientSideStorageItems, ClientSideStorageService } from '../../services/client-side-storage.service';
import { ClientSideStorageType } from '../../models/client-side-storage-type.enum';
import { IMicromobilityFilters, initialMicromobilityFilters, isValidMicromobilityFilters } from '../Shared/MicromobilityFilters/micromobility-filters.model';
import { CompareHelper } from '../../helpers/compare.helper';
import LogService from '../../services/log.service';
import React from 'react';
import MicromobilityHelper from './helpers/micromobility.helper';
import exclamationCircleYellowIcon from '../../../assets/icons/exclamation-circle-yellow.svg';
import DataUrlHelper from '../../helpers/data-url.helper';

function isTravelPlannerFiltersSaveEnabled() {
  return ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional) && ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave) !== '0';
}

function getTravelPlannerFilterFromLocalStorageMergedWith(initialTravelPlannerFilters: ITravelPlannerFilters) {
  if (!isTravelPlannerFiltersSaveEnabled()) return initialTravelPlannerFilters;

  const travelPlannerFilters = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters);
  if (travelPlannerFilters) {
    try {
      const parsedFilters = JSON.parse(travelPlannerFilters);
      return isValidTravelPlannerFilters(parsedFilters) ? { ...initialTravelPlannerFilters, ...parsedFilters } : initialTravelPlannerFilters;
    } catch (error) {
      LogService.logError(error, 'Error parsing travel planner filters from local storage');
    }
  }
  return initialTravelPlannerFilters;
}

function getMicromobilityFilterFromLocalStorageMergedWith(initialMicromobilityFilters: IMicromobilityFilters) {
  if (!isTravelPlannerFiltersSaveEnabled()) return initialMicromobilityFilters;

  const micromobilityFilters = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.MicroMobilityFilters);
  if (micromobilityFilters) {
    try {
      const parsedFilters = JSON.parse(micromobilityFilters);

      if (isValidMicromobilityFilters(parsedFilters)) {
        const merged = { ...initialMicromobilityFilters, ...parsedFilters };
        if (merged.showElectricBikesVoi !== merged.showElectricBikesDott) {
          merged.showElectricBikes = undefined;
        }
        if (merged.showScootersRyde !== merged.showScootersVoi || merged.showScootersRyde !== merged.showScootersDott || merged.showScootersVoi !== merged.showScootersDott) {
          merged.showScooters = undefined;
        }
        return merged;
      }
    } catch (error) {
      LogService.logError(error, 'Error parsing micromobility filters from local storage');
    }
  }
  return initialMicromobilityFilters;
}

function TravelPlanner({ initialShowSearchDetails, headingText, headerHtml }: { initialShowSearchDetails: boolean; headingText?: string; headerHtml?: string }) {
  // === CONFIGURATION ===
  // NV-5212
  const bussOnDemandEnabled = ConfigurationService.get('travelplanner.enablebussondemandintravelplannerfeatureflag') === 'true';
  const defaultInitialTravelPlannerFilters = bussOnDemandEnabled ? initialTravelPlannerFiltersWithBussOnDemand : initialTravelPlannerFilters;
  const currentInitialTravelPlannerFilters = getTravelPlannerFilterFromLocalStorageMergedWith(defaultInitialTravelPlannerFilters);
  const currentMicroMobilityFilters = FeatureFlagService.enableMicromobilitySearch ? getMicromobilityFilterFromLocalStorageMergedWith(initialMicromobilityFilters) : undefined;

  // === STATE ===
  const [fromToSelectorState, setFromToSelectorState] = useState<IFromToSelectorState>({
    value: {
      fromLocation: undefined,
      toLocation: undefined,
    },
    validationInfo: new ValidationInfo(),
  });
  const [selectedTimeMode, setSelectedTimeMode] = useState<TimeMode>(TimeMode.now);
  const [dateTimePickerState, setDateTimePickerState] = useState<IDateTimePickerState>({
    value: DateHelper.roundToNearestFiveMinutes(DateTime.now()),
    validationInfo: new ValidationInfo(),
  });
  // NV-5212
  const [selectedTravelPlannerFilters, setSelectedTravelPlannerFilters] = useState<ITravelPlannerFilters>(currentInitialTravelPlannerFilters);
  const [mostRecentlyUsedFilters, setMostRecentlyUsedFilters] = useState<ITravelPlannerFilters>(currentInitialTravelPlannerFilters);
  const [showSearchDetails, setShowSearchDetails] = useState<boolean>(initialShowSearchDetails);
  const [searchJourneysStatus, setSearchJourneysStatus] = useState<Status>(Status.notperformed);
  const [receivedJourneys, setReceivedJourneys] = useState<IJourney[]>([]);
  const [receivedPaginationLinks, setReceivedPaginationLinks] = useState<IPaginationLinks | undefined>(undefined);
  const [isSearchingPrevious, setIsSearchingPrevious] = useState<boolean>(false);
  const [isSearchingNext, setIsSearchingNext] = useState<boolean>(false);
  const [errorResponse, setErrorResponse] = useState<IErrorResponse | undefined>(undefined);
  const [selectedMicroMobilityFilters, setSelectedMicroMobilityFilters] = useState<IMicromobilityFilters | undefined>(currentMicroMobilityFilters);

  // === VARIABLES ===
  const validityTimeUntil = ConfigurationService.get('travelplanner.validitytimeuntil');
  const minDateTime = DateTime.now().startOf('day').minus({ days: 1 });
  const maxDateTime = validityTimeUntil ? DateTime.fromISO(validityTimeUntil) : DateTime.now().startOf('day').plus({ days: 90 });
  const uniqueJourneyDates = JourneyHelper.getAllUniqueDates(receivedJourneys);
  const hasLocalService = fromToSelectorState.value.fromLocation?.location?.hasLocalService || fromToSelectorState.value.toLocation?.location?.hasLocalService;
  const localServiceHtmlText = TranslationService.get('travelplanner.localservicetext');

  // === REFS ===
  const fromToSelectorRef = useRef<IFromToSelector>(null!);

  // === HELPER FUNCTIONS ===
  function getScreenReaderSearchStatus() {
    if (searchJourneysStatus === Status.notperformed) {
      return '';
    }
    if (searchJourneysStatus === Status.loading) {
      return TranslationService.get('common.searching');
    }
    return TranslationService.get('common.showingxresults').replace('{0}', (receivedJourneys.length ? receivedJourneys.length : 0).toString());
  }

  function getValidatableComponents(): ValidatableComponent[] {
    return [{ component: fromToSelectorRef.current, validationInfo: fromToSelectorState.validationInfo, setState: setFromToSelectorState }];
  }

  function showFeedbackForm() {
    const feedbackBlock = document.getElementById('travel-planner-feedback-form-block');
    if (feedbackBlock) {
      // Show feedback form
      document.getElementById('travel-planner-feedback-form-block')?.classList.remove('d-none');

      // Bind click event to close button
      document.getElementById('travel-planner-feedback-form-block-close-btn')?.addEventListener('click', () => {
        feedbackBlock.classList.add('d-none');
      });

      // Check if "thank you" message is shown, then hide it and show feedback form main content/question
      const feedbackStatusMessage = document.getElementsByClassName('Form__Status__Message')[0] as HTMLElement;
      if (feedbackStatusMessage && !feedbackStatusMessage.classList.contains('hide')) {
        const feedbackMainContent = document.getElementsByClassName('Form__MainBody')[0] as HTMLElement;
        if (feedbackMainContent) {
          // Show feedback form main content/question
          feedbackMainContent.style.display = '';
          // Hide status message
          feedbackStatusMessage.classList.add('hide');
          feedbackStatusMessage.classList.remove('Form__Success__Message');
        }
      }
    }
  }

  // === EVENT HANDLERS ===
  async function handleSearchClick(): Promise<void> {
    if (searchJourneysStatus === Status.loading) return;

    const [isFormValid] = ValidationHelper.checkValidity(getValidatableComponents());
    if (!isFormValid) {
      setSearchJourneysStatus(Status.notperformed);
      return;
    }

    setSearchJourneysStatus(Status.loading);
    fromToSelectorRef.current?.addSelectedItemsToAutocompleteHistory();
    setReceivedJourneys([]);
    setReceivedPaginationLinks(undefined);
    AnalyticsService.addEvent(AnalyticsService.JourneySearchCategory, 'Sök resa', '');

    const fromLocationDetails = fromToSelectorState.value.fromLocation ? JourneySearchHelper.getLocationParameters(fromToSelectorState.value.fromLocation) : undefined;
    const toLocationDetails = fromToSelectorState.value.toLocation ? JourneySearchHelper.getLocationParameters(fromToSelectorState.value.toLocation) : undefined;

    try {
      if (!fromLocationDetails || !toLocationDetails) {
        throw new Error('From and to locations are required.');
      }

      const result = FeatureFlagService.enableMicromobilitySearch
        ? await MicromobilityService.getJourneys(
            fromLocationDetails.gid,
            fromLocationDetails.name,
            fromLocationDetails.latitude,
            fromLocationDetails.longitude,
            toLocationDetails.gid,
            toLocationDetails.name,
            toLocationDetails.latitude,
            toLocationDetails.longitude,
            selectedTimeMode === TimeMode.now ? undefined : dateTimePickerState.value,
            selectedTimeMode === TimeMode.arrival ? DateTimeRelatesTo.arrival : DateTimeRelatesTo.departure,
            undefined,
            undefined,
            [
              ...selectedTravelPlannerFilters.transportModes,
              ...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []),
              ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : []),
            ],
            selectedTravelPlannerFilters.transportSubModes,
            selectedTravelPlannerFilters.onlyDirectConnections,
            selectedTravelPlannerFilters.viaStopArea?.gid,
            selectedTravelPlannerFilters.interchangeDuration === InterchangeDuration.short ? 2 : undefined,
            selectedTravelPlannerFilters.includeNearbyStopAreas,
            [...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []), ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : [])],
            selectedTravelPlannerFilters.bikingMaxDistance,
            selectedTravelPlannerFilters.bikingSpeed,
            selectedTravelPlannerFilters.showOccupancy,
            selectedMicroMobilityFilters
          )
        : await ApiPlaneraResaService.getJourneys(
            fromLocationDetails.gid,
            fromLocationDetails.name,
            fromLocationDetails.latitude,
            fromLocationDetails.longitude,
            toLocationDetails.gid,
            toLocationDetails.name,
            toLocationDetails.latitude,
            toLocationDetails.longitude,
            selectedTimeMode === TimeMode.now ? undefined : dateTimePickerState.value,
            selectedTimeMode === TimeMode.arrival ? DateTimeRelatesTo.arrival : DateTimeRelatesTo.departure,
            undefined,
            undefined,
            [
              ...selectedTravelPlannerFilters.transportModes,
              ...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []),
              ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : []),
            ],
            selectedTravelPlannerFilters.transportSubModes,
            selectedTravelPlannerFilters.onlyDirectConnections,
            selectedTravelPlannerFilters.viaStopArea?.gid,
            selectedTravelPlannerFilters.interchangeDuration === InterchangeDuration.short ? 2 : undefined,
            selectedTravelPlannerFilters.includeNearbyStopAreas,
            [...(selectedTravelPlannerFilters.showWalking ? [TransportMode.walk] : []), ...(selectedTravelPlannerFilters.showBiking ? [TransportMode.bike] : [])],
            selectedTravelPlannerFilters.bikingMaxDistance,
            selectedTravelPlannerFilters.bikingSpeed,
            selectedTravelPlannerFilters.showOccupancy
          );

      setReceivedJourneys(JourneyHelper.setJourneyIds(result.results, 0, false));
      setReceivedPaginationLinks(result.links);
      setSearchJourneysStatus(Status.success);
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
      AnalyticsService.addEvent(AnalyticsService.JourneySearchCategory, AnalyticsService.ErrorAction, `Sök resa - ${AnalyticsService.InternalErrorLabel}`);
    } finally {
      setMostRecentlyUsedFilters(selectedTravelPlannerFilters);
      showFeedbackForm();
    }
  }

  async function handlePreviousClick(): Promise<void> {
    if (receivedPaginationLinks?.previous === undefined) return;
    try {
      setIsSearchingPrevious(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.previous);
      setReceivedJourneys((ps) => JourneyHelper.setJourneyIds(result.results, ps.length > 0 ? ps[0].id - 1 : 0, true).concat(ps));
      setReceivedPaginationLinks((ps) => (ps !== undefined ? { ...ps, previous: result.links?.previous } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
    } finally {
      setIsSearchingPrevious(false);
    }
  }

  async function handleNextClick(): Promise<void> {
    if (receivedPaginationLinks?.next === undefined) return;
    try {
      setIsSearchingNext(true);
      const result = await ApiPlaneraResaService.getJourneysFromPagination(receivedPaginationLinks.next);
      setReceivedJourneys((ps) => ps.concat(JourneyHelper.setJourneyIds(result.results, ps.length > 0 ? ps[ps.length - 1].id + 1 : 0, false)));
      setReceivedPaginationLinks((ps) => (ps !== undefined ? { ...ps, next: result.links?.next } : undefined));
      setErrorResponse(undefined);
    } catch (error: unknown) {
      if (ErrorHandlerHelper.isErrorReponse(error)) {
        setErrorResponse(ErrorResponseHelper.enrichApiPlaneraResaErrorResponse(error as IErrorResponse, (x) => TranslationService.get(x)));
      }
      setSearchJourneysStatus(Status.failed);
    } finally {
      setIsSearchingNext(false);
    }
  }

  function saveTravelPlannerFilters(filtersToSave: ITravelPlannerFilters) {
    if (isTravelPlannerFiltersSaveEnabled() && filtersToSave) {
      const difference = CompareHelper.getDifferences(defaultInitialTravelPlannerFilters, filtersToSave, ['viaStopArea']);
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters, JSON.stringify(difference));
    }
  }

  function handleTravelPlannerFilterChange(newFilters: ITravelPlannerFilters) {
    saveTravelPlannerFilters(newFilters);
    setSelectedTravelPlannerFilters(newFilters);
  }

  function saveMicromobilityFilters(filtersToSave: IMicromobilityFilters | undefined) {
    if (isTravelPlannerFiltersSaveEnabled() && filtersToSave) {
      const difference = CompareHelper.getDifferences(initialMicromobilityFilters, filtersToSave, ['showStopAreas']);
      ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.MicroMobilityFilters, JSON.stringify(difference));
    }
  }

  function handleMicromobilityFilterChange(newFilters: IMicromobilityFilters) {
    saveMicromobilityFilters(newFilters);
    setSelectedMicroMobilityFilters(newFilters);
  }

  function handleTravelPlannerFiltersClear(): void {
    if (FeatureFlagService.enableMicromobilitySearch) {
      setSelectedMicroMobilityFilters(initialMicromobilityFilters);
      saveMicromobilityFilters(initialMicromobilityFilters);
    }
  }

  function handleTravelPlannerSaveSelectionsChange(newValue: boolean) {
    if (!newValue) {
      ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFilters);
      ClientSideStorageService.clearLocalStorage(ClientSideStorageItems.LocalStorage.MicroMobilityFilters);
    } else {
      saveMicromobilityFilters(selectedMicroMobilityFilters);
      saveTravelPlannerFilters(selectedTravelPlannerFilters);
    }
  }

  function getResultList(date: DateTime<boolean>): JSX.Element {
    return (
      <>
        <DateHeader date={date} uniqueJourneyDates={uniqueJourneyDates} />
        <ul className="list-unstyled">
          {JourneyHelper.getJourneysForDate(receivedJourneys, date).map((journey, index) => (
            <ExpandableListItem
              key={journey.id}
              id={`result-item-${index}`}
              className={JourneyHelper.getTripLegIsCancelled(journey) || JourneyHelper.getTripLegIsPartCancelled(journey) ? 'expandable-list-item--gray' : ''}
              collapsedContent={<JourneySummary journey={journey} useEstimatedTime={true} showNotesIcons={true} />}
              onExpandChange={(toBeExpanded) => {
                if (toBeExpanded) {
                  if (FeatureFlagService.enableMicromobilitySearch) {
                    MicromobilityHelper.trackExpandJourney(journey);
                  } else {
                    AnalyticsService.addEvent(AnalyticsService.TravelPlannerCategory, 'Visa detaljvy', '');
                  }
                }
              }}
              renderExpandedContent={(isVisible) => <JourneyDetails journey={journey} isVisible={isVisible} />}
            ></ExpandableListItem>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="travel-planner position-relative" role="search" aria-label={TranslationService.get('travelplanner.searchlabel')}>
      <div className={`container travel-planner__search-form pb-3 pt-3 pt-md-5 ${showSearchDetails ? 'pb-md-0' : 'pb-md-5'}`}>
        <div className="row justify-content-lg-center">
          <div className="col-lg-8">
            <div className="px-3 px-md-0 py-3 py-md-0 travel-planner__search-form-inner-wrapper rounded-xl bg-white">
              <div className={`px-md-4`}>
                <div>
                  {headingText && (
                    <div className="text-center pb-3 pb-sm-4">
                      <h1 className="m-0 travel-planner__heading pt-md-3">{headingText}</h1>
                    </div>
                  )}
                  {headerHtml && <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(headerHtml) }} className="mb-3 pt-md-3"></div>}
                  <div className="mb-3 mb-md-0 pb-0 pb-md-4 m-0">
                    <FromToSelector
                      id={'from-to-selector'}
                      ref={fromToSelectorRef}
                      locationTypes={[LocationType.stopArea, LocationType.metaStation, LocationType.address, LocationType.pointOfInterest]}
                      placeholderText={TranslationService.get('travelplanner.stopaddresspoi')}
                      enableCurrentPosition={true}
                      analyticsCategory={AnalyticsService.TravelPlannerCategory}
                      state={fromToSelectorState}
                      onChange={(value) => setFromToSelectorState({ value, validationInfo: new ValidationInfo() })}
                      onFocus={() => setShowSearchDetails(true)}
                    />
                  </div>
                </div>
              </div>
              <div className={`px-md-4 pb-md-4 ${showSearchDetails ? '' : 'd-md-none'}`}>
                <div className="mb-3">
                  <TimeModeSelector
                    id="time-mode-selector"
                    availableTimeModes={[TimeMode.now, TimeMode.departure, TimeMode.arrival]}
                    selectedTimeMode={selectedTimeMode}
                    legend={TranslationService.get('travelplanner.selectdatetimeheading')}
                    onChange={setSelectedTimeMode}
                  />
                </div>
                {selectedTimeMode !== TimeMode.now && (
                  <div className="search-tools__pickers mb-3 mb-md-4">
                    <DateTimePicker
                      minDateTime={minDateTime}
                      maxDateTime={maxDateTime}
                      showAsteriskOnRequiredFields={false}
                      state={dateTimePickerState}
                      layoutSmRow={true}
                      onChange={(value) => {
                        setDateTimePickerState({ ...dateTimePickerState, value });
                      }}
                    />
                  </div>
                )}
                {FeatureFlagService.enableMicromobilitySearch && selectedMicroMobilityFilters && (
                  <fieldset className="mb-3" aria-describedby="micromobility-filters-help-text">
                    <legend className="mb-0">{TranslationService.get('travelplanner.showmicromobility')}</legend>
                    <small id="micromobility-filters-help-text" className="d-block travel-planner__micromobility-filters-help-text mb-2">
                      <img src={DataUrlHelper.encode(exclamationCircleYellowIcon)} alt="" className="radio-button__help-icon me-1" />
                      {TranslationService.get('travelplanner.micromobility.delaycompensationhelptext')}
                    </small>
                    <MicromobilityFilters id="micro-mobility-filters" showSelectionStopAreas={false} selection={selectedMicroMobilityFilters} onChange={handleMicromobilityFilterChange} />
                  </fieldset>
                )}

                <TravelPlannerFilters
                  mostRecentlyUsed={mostRecentlyUsedFilters}
                  state={selectedTravelPlannerFilters}
                  onChange={handleTravelPlannerFilterChange}
                  onSaveSelectionsChange={handleTravelPlannerSaveSelectionsChange}
                  onClear={handleTravelPlannerFiltersClear}
                />
                <div className="d-grid mx-auto col-md-7">
                  <button id="searchbutton" className="btn btn-primary" disabled={searchJourneysStatus === Status.loading} type="button" onClick={handleSearchClick}>
                    {TranslationService.get('common.searchtrip')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container travel-planner__results">
        <span className="visually-hidden" aria-live="polite">
          {getScreenReaderSearchStatus()}
        </span>
        {searchJourneysStatus === Status.loading && <Spinner className="mt-4" />}
        <div className="row justify-content-center">
          <GeneralInformation
            showInformation={
              (receivedJourneys.length === 0 && searchJourneysStatus === Status.success) || (searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.information)
            }
            informationText={errorResponse?.errorMessage ?? TranslationService.get('travelplanner.searcherrornotrips')}
            className="mt-4 text-center col-lg-8"
          />
        </div>
        {searchJourneysStatus === Status.success && (
          <>
            {receivedJourneys.length > 0 && (
              <div className="mt-0 mt-md-3 ">
                {FeatureFlagService.enableMicromobilitySearch ? (
                  uniqueJourneyDates.map((date) => (
                    <div key={date.toISODate()} className="fullwidth-sm">
                      <ul className="bg-blue paginated-list__results list-unstyled">
                        <li>{getResultList(date)}</li>
                      </ul>
                    </div>
                  ))
                ) : (
                  <PaginatedList
                    className="fullwidth-sm"
                    previousButtonDisabled={receivedPaginationLinks?.previous === undefined}
                    nextButtonDisabled={receivedPaginationLinks?.next === undefined}
                    showSpinnerTop={isSearchingPrevious}
                    showSpinnerBottom={isSearchingNext}
                    onPreviousClick={handlePreviousClick}
                    onNextClick={handleNextClick}
                    preListItem={
                      hasLocalService &&
                      localServiceHtmlText && (
                        <div className="text-center last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(localServiceHtmlText) }}></div>
                      )
                    }
                  >
                    {uniqueJourneyDates.map((date) => (
                      <React.Fragment key={date.toISODate()}>{getResultList(date)}</React.Fragment>
                    ))}
                  </PaginatedList>
                )}
              </div>
            )}
          </>
        )}
        {searchJourneysStatus === Status.failed && hasLocalService && localServiceHtmlText && (
          <div className="row">
            <div className="col-lg-8 offset-lg-2">
              <div className="bg-blue border-blue p-2 mt-3">
                <div className="text-center last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(localServiceHtmlText) }}></div>
              </div>
            </div>
          </div>
        )}
        <div className="row justify-content-center">
          <GeneralError
            showError={searchJourneysStatus === Status.failed && errorResponse?.errorLevel === ErrorLevel.error}
            errorText={errorResponse?.errorMessage}
            className="mt-4 text-center col-lg-8"
          />
        </div>
      </div>
    </div>
  );
}

export default TravelPlanner;
